import chain from "lodash/chain";
import defaults from "lodash/defaults";
import find from "lodash/find";
import get from "lodash/get";
import moment from "moment";
// Constants
import { DATE_FORMATS } from "../../../constants";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import CalendarAddExerciseModal from "../../../parts/modals/calendar-add-exercise";
import CoachRecordVideoModal from "../../../parts/modals/coach-record-video";
import ImportWorkoutModal from "../../../parts/modals/import-workout";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import ProgressionModal from "../../../parts/modals/progression";
import ShareModal from "../../../parts/modals/share";
import SwapExerciseModal from "../../../parts/modals/swap-exercise";
import VideoRecordTutorialModal from "../../../parts/modals/VideoRecordTutorialModal";
import {
  buildPreviousOutcomesMap,
  TREND_LOOKBACK_WEEKS,
} from "../../../utils/calendar-trends";
import getDayRangeString from "../../../utils/day-range-string";
import { loadClientConfigurationForAthlete } from "../../../utils/load-athlete-client-configuration";
import { dayProgramProjection } from "../../../utils/projection-helpers";
import { resolveExerciseSetVideoModal } from "../../../utils/video-modal-url";
import CalendarDay from "./CalendarDay";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const { DATE_DAY_FORMAT, UTC } = DATE_FORMATS;

const CalendarDayController = async (
  { req, res, locals, load, api, fail },
  next
) => {
  if (req.query.view !== "day") return next();
  const { query = {} } = req;
  // locals.athlete whose calendar is being rendered. This has been preloaded in the ensureCoach middleware
  const units = locals.athlete ? locals.athlete.units : locals.units;
  const modal = get(query, "modal", {});

  defaults(query, {
    units,
    date: moment.utc(query.date).format(DATE_DAY_FORMAT),
    zoneType: "total",
    relTrends: false,
  });
  const { dataView } = query;
  locals.originalQueryDate = `${query.date}`;
  locals.activeWeek = moment(query.date, DATE_DAY_FORMAT).startOf("week");

  if (query.date instanceof Date)
    query.date = moment.utc(query.date).format(DATE_DAY_FORMAT);
  const startDate = moment.utc(query.date, DATE_DAY_FORMAT).startOf("day");
  const endDate = moment.utc(query.date, DATE_DAY_FORMAT).endOf("day");
  const loadPromises = [
    load("activeExercises", {
      start: startDate,
      end: endDate,
      proxyAthlete: query.athlete,
      reqSource: "calendar",
    }),
    load("activeSections", {
      start: startDate,
      end: endDate,
      proxyAthlete: query.athlete,
      reqSource: "calendar",
    }),
    load("currentYearExercises", {
      start: startDate,
      end: endDate,
      proxyAthlete: query.athlete,
      reqSource: "calendar",
    }),
    load("workoutNote", locals.originalQueryDate, query.athlete),
    load(
      "workoutCoachNote",
      locals.originalQueryDate,
      query.athlete,
      query.organizationCoach || locals.activeUser._id
    ),
    load("videoRecord", locals.originalQueryDate, query.athlete, "calendar"),
    // Exact-day match: feeds PersonalRecordsCard (via QuickMetrics), which
    // has no date filtering of its own and expects only PRs literally set
    // on this specific day.
    load("dayPersonalRecords", {
      end: moment
        .utc(locals.query.date, DATE_DAY_FORMAT)
        .endOf("day")
        .format(UTC),
      proxyAthlete: query.athlete,
    }),
    // Wide range: feeds the badge-matching logic in OutcomesComponent. A PR
    // is recognized retroactively once enough history exists, so it can
    // only be found by range-matching against "today", not by requiring its
    // recorded date to equal this exact day. Day view has no natural
    // "start" of its own, so reach all the way back to the platform epoch
    // (matching the default used server-side in personal-records.js).
    load("dayPersonalRecordsRange", {
      start: moment.utc("2017-01-01", UTC).format(UTC),
      end: moment
        .utc(locals.query.date, DATE_DAY_FORMAT)
        .endOf("day")
        .format(UTC),
      proxyAthlete: query.athlete,
      isCheckIn: true,
    }),
    load("dailyStats", {
      proxyAthlete: query.athlete,
      date: locals.originalQueryDate,
    }),
    load("nutritionPlans", {
      proxyAthlete: query.athlete,
    }),
  ];
  if (dataView && dataView === "mealPhotos" && query.utcDate) {
    const dateStr = query.utcDate.toString();

    loadPromises.push(
      load("mealPhotos", {
        startDate: dateStr,
        endDate: dateStr,
      })
    );
  }
  await Promise.all(loadPromises);

  if (query.athlete) {
    await loadClientConfigurationForAthlete(load, query.athlete);
  }
  if (query.relTrends) {
    const trendHistoryStart = startDate
      .clone()
      .subtract(TREND_LOOKBACK_WEEKS, "weeks");
    const trendHistoryEnd = startDate.clone().subtract(1, "day");
    const trendHistory = await load("exerciseTrendHistory", {
      start: trendHistoryStart,
      end: trendHistoryEnd,
      proxyAthlete: query.athlete,
    });
    locals.previousOutcomes = buildPreviousOutcomesMap(
      locals.activeExercises || [],
      trendHistory || [],
      locals.activeUser.startingCalendarDay || 0
    );
  } else {
    locals.previousOutcomes = null;
  }

  if (locals.activeExercises) {
    const exerciseWithCycle = locals.activeExercises.find((e) => e.cycle) || {};
    locals.cycleDetail = exerciseWithCycle.cycle;
    const cycleId = get(locals, "cycleDetail.id", false);
    if (cycleId)
      await Promise.all([
        load("editCycle", {
          cycle: cycleId,
          proxyAthlete: query.athlete,
          customProjection: dayProgramProjection,
        }),
        load("editExercises", {
          cycle: cycleId,
          isScheduled: true,
          proxyAthlete: query.athlete,
          fields: ["cycle", "date", "_id", "type"],
        }),
      ]);
  }

  if (modal.type === "share" && !modal.snapshot) {
    // create share link.
    try {
      modal.snapshot = (
        await (
          await api("snapshot", {
            method: "POST",
            body: {
              type: "workout",
              data: JSON.stringify(
                chain(locals)
                  .pick(["activeExercises"])
                  .assign({ start: startDate.toJSON(), end: endDate.toJSON() })
                  .value()
              ),
            },
          })
        ).json()
      )._id;

      modal.date = getDayRangeString([startDate, endDate]);

      return res.refreshWith({ modal });
    } catch (e) {
      console.error(e.stack || e);
      fail(400, "Error creating share link.");
    }
  }

  if (modal.type === "calendar-add-exercise" && query.addType === "complex") {
    await load("complexes");
  }

  if (modal.type === "import-workout") {
    await Promise.all([load("savedWorkouts"), load("workoutFolders")]);
  }

  const editExercise = query.editMode
    ? find(locals.activeExercises, { _id: query.exercise })
    : {};

  const getCoachVideoRecord = () => {
    if (locals.videoRecord) return locals.videoRecord;
    return null;
  };

  const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
    locals.organizationRoleName
  );
  if (
    locals.activeUser.organizationId &&
    isAllowToViewAllOrganizationsAthletes &&
    !locals.allAthletesFromOrganization
  ) {
    await load("allAthletesFromOrganization");
  }
  let comesFromPR = false;

  // "View Video" (exercise-set-video) - see resolveExerciseSetVideoModal's
  // own doc comment for why this needs more than a plain prop pass-through.
  const { redirectParams, resolvedThreadId, prevHref, nextHref } =
    await resolveExerciseSetVideoModal({
      query,
      modal,
      locals,
      load,
      athlete: query.athlete,
    });
  if (redirectParams) return res.refreshWith(redirectParams);

  comesFromPR = req?.original?.headers?.referer?.includes("personal-record");

  locals.modal = {
    "exercise-set-video": (
      <JWPlayerVideoModal
        athleteId={query.uploaderId || query.athlete}
        exerciseToEdit={query.exerciseToEdit}
        locals={locals}
        mediaId={query.mediaId}
        movement={query.movement}
        originalId={query.originalId}
        primary={query.primary}
        reviewedByTheCoach={query.reviewedByTheCoach}
        secondary={query.secondary}
        setIndex={query.setIndex}
        threadId={resolvedThreadId}
        type={query.type}
        utcDate={query.utcDate}
        videoIndex={query.videoIndex}
        prevHref={prevHref}
        nextHref={nextHref}
        setNumber={query.setNumber}
        setTarget={query.setTarget}
        setComment={query.setComment}
      />
    ),
    "calendar-add-exercise": (
      <CalendarAddExerciseModal
        editExercise={editExercise}
        reqSource="calendar"
      />
    ),
    share: <ShareModal />,
    progression: <ProgressionModal />,
    "video-record-tutorial": <VideoRecordTutorialModal />,
    "swap-exercise": <SwapExerciseModal exercise={editExercise} />,
    "import-workout": (
      <ImportWorkoutModal
        savedWorkouts={locals.savedWorkouts || []}
        workoutFolders={locals.workoutFolders || []}
        date={query.day}
        exercisesLength={query.exercisesLength}
        highestOrder={query.highestOrder}
        athlete={locals.athlete || locals.activeUserToRequest}
      />
    ),
    "coach-record-video": (
      <CoachRecordVideoModal videoRecord={getCoachVideoRecord()} />
    ),
    "activity-feed": <ActivityDrawer />,
  }[modal.type];
  res.body = (
    <CalendarDay
      comesFromPR={comesFromPR}
      editedExercise={query.editExerciseId}
      setIndex={query.setIndex}
    />
  );
};

export default CalendarDayController;
